import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Heart, Calendar, MapPin, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { templeInfo, programs, events } from '../data/mock';
import TotalViewsInline from '../components/TotalViewsInline';

const Home = () => {
  return (
    <div className="min-h-screen bg-[#e6f0f8] text-stone-900">
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-screen flex items-center overflow-hidden"
        style={{
          backgroundImage:
            'url(https://customer-assets.emergentagent.com/job_alabama-viharaya/artifacts/0vziwkp0_Buddha_Home_Screen_2000px.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* keep image vivid: only a soft left vignette so text reads, no top-down wash */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/25 to-transparent" />
        {/* subtle bottom blend into next section (small only) */}
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-[#7c2d12]/60" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-sky-400/15 blur-[160px]" />

        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 py-24 w-full">
          <div className="fade-up">
            <div className="flex items-center gap-3 text-white tracking-[0.3em] text-[11px] uppercase mb-6">
              <span className="h-px w-12 bg-white/70" />
              <Sparkles size={14} />
              <span>Theravāda · Sanctuary · Sangha</span>
            </div>
            <h1
              className="font-serif-display text-5xl md:text-7xl lg:text-[88px] leading-[1.02] tracking-tight text-white max-w-4xl"
              style={{ textShadow: '0 4px 35px rgba(0,0,0,0.7), 0 2px 10px rgba(0,0,0,0.5)' }}
            >
              A still place<br />
              for a <span className="italic text-white">restless world.</span>
            </h1>
            <p
              className="mt-8 max-w-xl text-lg md:text-xl text-stone-100 leading-relaxed font-light"
              style={{ textShadow: '0 2px 14px rgba(0,0,0,0.7)' }}
            >
              Alabama Buddhist Vihāra — a Theravāda temple for meditation, teachings, and the
              quiet unfolding of wisdom.
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-5">
              <Link to="/programs">
                <Button
                  data-testid="hero-explore-btn"
                  className="group bg-sky-600 hover:bg-sky-700 text-white px-8 py-6 rounded-full text-base font-medium tracking-wide shadow-lg shadow-sky-900/30"
                >
                  Explore Programs
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                </Button>
              </Link>
              <Link to="/donate">
                <Button
                  variant="outline"
                  data-testid="hero-donate-btn"
                  className="bg-white/10 backdrop-blur-sm border border-white/40 hover:border-sky-300 text-white hover:text-sky-200 hover:bg-white/15 px-8 py-6 rounded-full text-base font-medium tracking-wide"
                >
                  <Heart className="mr-2" size={18} /> Offer Dāna
                </Button>
              </Link>
            </div>
          </div>

          {/* hero meta strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-3 gap-6 max-w-3xl border-t border-white/25 pt-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/80 mb-2">Tradition</div>
              <div className="text-lg md:text-xl font-serif-display text-white" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>Theravāda</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/80 mb-2">Meditation</div>
              <div className="text-lg md:text-xl font-serif-display text-white" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>Wed & Sun · 6pm</div>
            </div>
            <TotalViewsInline />
          </div>
        </div>
      </section>

      {/* QUOTE — deep sapphire sky to match Buddha hero */}
      <section
        className="relative py-32 overflow-hidden border-t border-sky-800/40"
        style={{
          background:
            'radial-gradient(circle at 80% 20%, rgba(56,189,248,0.28) 0%, transparent 55%), radial-gradient(circle at 10% 80%, rgba(30,64,175,0.4) 0%, transparent 55%), linear-gradient(135deg, #0f2847 0%, #1e3a5f 100%)',
        }}
      >
        {/* Buddha statue accent on the right */}
        <div
          className="hidden md:block absolute right-0 top-0 bottom-0 w-1/2 opacity-30"
          style={{
            backgroundImage:
              'url(https://customer-assets.emergentagent.com/job_alabama-viharaya/artifacts/0vziwkp0_Buddha_Home_Screen_2000px.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            maskImage: 'linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 80%)',
            WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 80%)',
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="text-sky-200 text-[11px] tracking-[0.3em] uppercase mb-8">
            — A Reflection —
          </div>
          <p
            className="font-serif-display italic text-3xl md:text-5xl text-white leading-[1.2] tracking-tight"
            style={{ textShadow: '0 4px 25px rgba(0,0,0,0.5)' }}
          >
            "Peace comes from within.<br />
            <span className="text-sky-200">Do not seek it without."</span>
          </p>
          <div className="mt-10 flex items-center justify-center gap-4">
            <span className="h-px w-10 bg-sky-300/70" />
            <span className="text-sm tracking-[0.3em] text-sky-200 uppercase">The Buddha</span>
            <span className="h-px w-10 bg-sky-300/70" />
          </div>
        </div>
      </section>

      {/* MONKS — warm honey gradient */}
      <section
        className="py-28 border-t border-sky-800/30"
        style={{
          background:
            'linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%)',
        }}
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-20">
            <div className="text-sky-800 text-[11px] tracking-[0.3em] uppercase mb-3">
              Our Spiritual Guides
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900">
              Meet our resident monks
            </h2>
            <p className="mt-4 text-stone-700 max-w-xl mx-auto leading-relaxed">
              Carriers of the lineage, teachers of the practice, friends on the path.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-5xl mx-auto">
            {/* Chief */}
            <div
              className="group relative rounded-2xl overflow-hidden border border-sky-500/40 transition-all duration-500 p-10 text-center"
              style={{
                background:
                  'linear-gradient(160deg, #ffffff 0%, #dbeafe 100%)',
                boxShadow:
                  '0 1px 0 rgba(255,255,255,0.7) inset, 0 22px 50px -30px rgba(120,80,20,0.45)',
              }}
            >
              <div className="absolute -top-24 -right-24 w-72 h-72 bg-sky-400/25 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="relative mx-auto w-44 h-44 mb-7">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-sky-200 to-sky-500 blur-2xl opacity-50 group-hover:opacity-70 transition-opacity" />
                <img
                  src="https://customer-assets.emergentagent.com/job_alabama-viharaya/artifacts/8w5kqe6t_photo_2026-03-29_08-30-20.jpg"
                  alt="Bhante Dhammaloka"
                  className="relative w-44 h-44 rounded-full object-contain bg-white border-2 border-sky-500 shadow-2xl"
                />
              </div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-sky-800 mb-2">
                Chief Incumbent
              </div>
              <h3 className="font-serif-display text-3xl text-stone-900 mb-4">Bhante Dhammaloka</h3>
              <div className="mx-auto h-px w-12 bg-sky-700/70 mb-5" />
              <p className="text-stone-700 leading-relaxed">
                Our spiritual leader, guiding the temple and community with wisdom and compassion.
              </p>
            </div>

            {/* Resident */}
            <div
              className="group relative rounded-2xl overflow-hidden border border-sky-500/40 transition-all duration-500 p-10 text-center"
              style={{
                background:
                  'linear-gradient(160deg, #ffffff 0%, #dbeafe 100%)',
                boxShadow:
                  '0 1px 0 rgba(255,255,255,0.7) inset, 0 22px 50px -30px rgba(120,80,20,0.45)',
              }}
            >
              <div className="absolute -top-24 -left-24 w-72 h-72 bg-sky-400/25 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="relative mx-auto w-44 h-44 mb-7">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-sky-200 to-sky-500 blur-2xl opacity-50 group-hover:opacity-70 transition-opacity" />
                <img
                  src="https://customer-assets.emergentagent.com/job_alabama-viharaya/artifacts/jubyiin4_photo_2026-03-22_14-20-28.jpg"
                  alt="Bhante Indasara"
                  className="relative w-44 h-44 rounded-full object-contain bg-white border-2 border-sky-500 shadow-2xl"
                />
              </div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-sky-800 mb-2">
                Resident Monk
              </div>
              <h3 className="font-serif-display text-3xl text-stone-900 mb-4">Bhante Indasara</h3>
              <div className="mx-auto h-px w-12 bg-sky-700/70 mb-5" />
              <p className="text-stone-700 leading-relaxed">
                A dedicated teacher of meditation, offering spiritual guidance to our community.
              </p>
            </div>
          </div>

          {/* Welcome Card */}
          <div
            className="mt-20 max-w-3xl mx-auto rounded-2xl border border-sky-500/40 p-10 md:p-12 text-center"
            style={{
              background:
                'linear-gradient(135deg, #ffffff 0%, #dbeafe 100%)',
              boxShadow:
                '0 1px 0 rgba(255,255,255,0.7) inset, 0 22px 50px -28px rgba(120,80,20,0.45)',
            }}
          >
            <div className="text-sky-800 text-[11px] tracking-[0.3em] uppercase mb-3">
              You are welcome here
            </div>
            <h3 className="font-serif-display text-3xl text-stone-900 mb-4">Visit the temple</h3>
            <p className="text-stone-700 leading-relaxed mb-8 max-w-xl mx-auto">
              Whether you are new to the path or a seasoned practitioner — our doors are open.
            </p>
            <div className="grid sm:grid-cols-3 gap-5 text-left">
              <div className="rounded-xl border border-sky-500/40 bg-white/70 p-5">
                <MapPin className="text-sky-700 mb-3" size={18} />
                <div className="text-[10px] tracking-widest uppercase text-stone-500 mb-1">Address</div>
                <div className="text-stone-800 text-sm leading-relaxed">2100 Hardwick Road<br />Pell City, Alabama 35128</div>
              </div>
              <div className="rounded-xl border border-sky-500/40 bg-white/70 p-5">
                <Phone className="text-sky-700 mb-3" size={18} />
                <div className="text-[10px] tracking-widest uppercase text-stone-500 mb-1">Phone</div>
                <a href={`tel:${templeInfo.phone}`} className="block text-stone-800 text-sm hover:text-sky-800">{templeInfo.phone}</a>
                <a href={`tel:${templeInfo.phone2}`} className="block text-stone-800 text-sm hover:text-sky-800">{templeInfo.phone2}</a>
              </div>
              <div className="rounded-xl border border-sky-500/40 bg-white/70 p-5">
                <Mail className="text-sky-700 mb-3" size={18} />
                <div className="text-[10px] tracking-widest uppercase text-stone-500 mb-1">Email</div>
                <a href={`mailto:${templeInfo.email}`} className="text-stone-800 text-sm hover:text-sky-800 break-all">{templeInfo.email}</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAMS — deep sapphire */}
      <section
        className="py-28 border-t border-sky-800/40"
        style={{
          background:
            'linear-gradient(180deg, #1e3a5f 0%, #0f2847 100%)',
        }}
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="mb-16 flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="text-sky-100 text-[11px] tracking-[0.3em] uppercase mb-3">
                Our Practice
              </div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-white" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.25)' }}>
                A sacred space for everyone
              </h2>
            </div>
            <Link
              to="/programs"
              className="text-sm text-sky-100 hover:text-white inline-flex items-center gap-2 group"
            >
              View all programs
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {programs.map((program, idx) => (
              <div
                key={program.id}
                data-testid={`program-card-${idx}`}
                className="group p-7 rounded-2xl border border-sky-300/30 bg-white/10 backdrop-blur-sm hover:bg-white/20 hover:border-sky-200/60 transition-all"
              >
                <div className="text-3xl font-serif-display text-sky-200 mb-5">
                  0{program.id}
                </div>
                <h3 className="font-serif-display text-xl text-white mb-3 leading-tight">
                  {program.title}
                </h3>
                <p className="text-sky-50/90 text-sm leading-relaxed mb-4">
                  {program.description}
                </p>
                {program.schedule && (
                  <div className="text-[11px] tracking-widest uppercase text-sky-200">
                    {program.schedule}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS — temple background with deep blue overlay */}
      <section
        className="relative py-28 overflow-hidden border-t border-sky-800/40"
        style={{
          backgroundImage:
            'url(https://customer-assets.emergentagent.com/job_alabama-viharaya/artifacts/0vziwkp0_Buddha_Home_Screen_2000px.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1e3d]/92 via-[#122b52]/90 to-[#0f2847]/95" />
        <div className="absolute -top-32 right-1/4 w-[500px] h-[500px] rounded-full bg-sky-400/20 blur-[140px]" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="text-sky-300 text-[11px] tracking-[0.3em] uppercase mb-3">
              Coming Up
            </div>
            <h2
              className="font-serif-display text-4xl md:text-5xl text-white"
              style={{ textShadow: '0 4px 25px rgba(0,0,0,0.5)' }}
            >
              Upcoming events
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {events.map((event) => (
              <div
                key={event.id}
                className={`p-8 rounded-2xl border backdrop-blur-md transition-all hover:-translate-y-1 ${
                  event.featured
                    ? 'border-sky-300/60 bg-gradient-to-br from-sky-400/25 to-sky-700/15'
                    : 'border-white/20 bg-white/10 hover:bg-white/15 hover:border-sky-300/40'
                }`}
              >
                {event.featured && (
                  <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-sky-200 mb-4">
                    <Sparkles size={11} /> Featured
                  </div>
                )}
                <h3 className="font-serif-display text-2xl text-white mb-3 leading-tight">
                  {event.title}
                </h3>
                {event.date && (
                  <div className="text-sky-200 text-sm tracking-wide mb-4">{event.date}</div>
                )}
                <p className="text-stone-100/90 text-sm leading-relaxed">{event.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-14">
            <Link to="/events">
              <Button className="bg-sky-600 hover:bg-sky-500 text-white px-8 py-5 rounded-full text-sm tracking-wide shadow-lg shadow-sky-900/40">
                View All Events <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA — temple image with deep blue overlay */}
      <section
        className="relative py-32 overflow-hidden border-t border-sky-800/30"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/6689733/pexels-photo-6689733.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b1e3d]/85 via-[#122b52]/75 to-[#1e3a5f]/85" />
        <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-sky-400/25 blur-[160px]" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="text-sky-300 text-[11px] tracking-[0.3em] uppercase mb-5">
            Begin
          </div>
          <h2
            className="font-serif-display text-4xl md:text-6xl text-white leading-tight mb-6"
            style={{ textShadow: '0 4px 30px rgba(0,0,0,0.6)' }}
          >
            Your journey toward<br />
            <span className="italic text-sky-200">inner peace.</span>
          </h2>
          <p
            className="text-lg text-stone-100 leading-relaxed mb-12 max-w-2xl mx-auto"
            style={{ textShadow: '0 2px 15px rgba(0,0,0,0.5)' }}
          >
            Whether you're new to the Dhamma or a seasoned practitioner, our doors are always open.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-sky-600 hover:bg-sky-500 text-white px-8 py-6 rounded-full text-base shadow-lg shadow-sky-900/40">
                Get in Touch <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/dana-schedule">
              <Button
                variant="outline"
                className="bg-white/10 backdrop-blur-sm border border-white/40 hover:border-sky-300 text-white hover:text-sky-200 hover:bg-white/15 px-8 py-6 rounded-full text-base"
              >
                <Calendar className="mr-2" size={18} /> View Dāna Schedule
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
